/**
 * Defensive guard for warehouse-sourced URLs rendered as links (e.g.
 * `security_settings_url` on `RepoCompliance`/`OrgRolloutSummary`). A
 * poisoned row could otherwise surface a `javascript:` link straight into
 * `<Link>`. Only http(s) URLs are considered safe; anything else — including
 * unparseable strings — returns null so the caller can fall back to plain
 * text.
 */
export function toSafeHttpHref(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
      return parsed.toString();
    }
  } catch {
    // invalid URL
  }
  return null;
}

/**
 * Any `scheme:` prefix at all, allowlisted or not.
 *
 * Exported because `utils/remediationDoc.ts` needs the same test for the same
 * reason, and kept here because this is the module that owns "which hrefs are
 * safe to hand to a link". Two copies is how the two come to disagree, which
 * matters more than it looks: the pattern requires a leading scheme letter, so
 * it deliberately does not match a protocol-relative `//host/path`, and every
 * caller has to handle that case itself.
 */
export const SCHEME_LIKE = /^[a-z][a-z0-9+.-]*:/i;
const HTTP_HREF = /^https?:\/\//i;

/**
 * Same http(s)-only guard as `toSafeHttpHref`, but for warehouse-sourced
 * fields that are stored without a protocol (e.g. a bare `example.com`
 * ticket link) — schemeless input is assumed https rather than rejected as
 * unparseable. Returns the (possibly https-prefixed) input string rather
 * than the normalized `URL#toString()` output, since callers display the
 * original value alongside the href.
 *
 * Input with an explicit non-http(s) scheme (`javascript:`, `data:`,
 * `ftp:`, ...) is rejected outright rather than falling through to the
 * https:// prepend below — otherwise e.g. `javascript:80` would become the
 * syntactically-valid `https://javascript:80` and pass. This also rejects
 * bare `host:port` values (e.g. `localhost:3000`), since they're
 * indistinguishable from a scheme prefix; report/ticket links are always
 * full external URLs, so that's an acceptable trade-off for an otherwise
 * ambiguous case.
 */
export function toSafeHref(url: string): string | null {
  const candidate = url.trim();
  if (SCHEME_LIKE.test(candidate) && !HTTP_HREF.test(candidate)) {
    return null;
  }
  const withScheme = HTTP_HREF.test(candidate)
    ? candidate
    : `https://${candidate}`;
  try {
    const parsed = new URL(withScheme);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
      return withScheme;
    }
  } catch {
    // invalid URL
  }
  return null;
}
